import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/lib/customerAuth";
import { formatPKR } from "@/lib/api";
import { LogOut, Package } from "lucide-react";

const STATUS_STYLE = {
  Pending: "bg-amber-100 text-amber-800",
  Confirmed: "bg-sage/15 text-sage-dark",
  Shipped: "bg-rose/15 text-rose-dark",
  Delivered: "bg-ink/10 text-ink",
  Cancelled: "bg-red-100 text-red-700",
};

export default function CustomerAccount() {
  const { customer, loading, logout, myOrders } = useCustomerAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [oLoading, setOLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!customer) {
      navigate("/login?next=/account");
      return;
    }
    myOrders().then(setOrders).finally(() => setOLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, loading]);

  if (loading || !customer) return <div className="text-center py-20 text-ink-soft">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-5 py-10 sm:py-16" data-testid="customer-account-page">
      <div className="flex justify-between items-start gap-4 mb-8 sm:mb-10">
        <div className="min-w-0">
          <div className="label-eyebrow !text-[10px] sm:!text-xs mb-2">My Account</div>
          <h1 className="font-display text-[1.9rem] sm:text-4xl text-ink">Hi, {customer.full_name.split(" ")[0]}</h1>
          <p className="text-ink-soft text-xs sm:text-sm mt-1 break-words">{customer.email} · {customer.phone}</p>
        </div>
        <button
          onClick={() => { logout(); navigate("/"); }}
          className="inline-flex items-center gap-2 shrink-0 min-h-[44px] text-xs uppercase tracking-widest text-ink-soft hover:text-rose-dark border border-border-subtle px-3 hover:border-rose"
          data-testid="customer-logout"
        >
          <LogOut size={12} /> Sign out
        </button>
      </div>

      <h2 className="font-display text-2xl text-ink mb-4 flex items-center gap-2"><Package size={18} className="text-sage" /> Your Orders</h2>
      {oLoading ? (
        <div className="text-ink-soft">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="bg-cream-warm border border-border-subtle p-10 text-center">
          <p className="text-ink-soft mb-4">You haven't placed any orders yet.</p>
          <Link to="/" className="inline-block bg-sage hover:bg-sage-dark text-cream px-6 py-3 text-xs uppercase tracking-widest font-semibold transition-colors">
            Start Shopping
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {orders.map((o) => (
            <li key={o.id}>
              <Link to={`/order/${o.id}`} className="block bg-white border border-border-subtle p-4 hover:border-sage transition-colors" data-testid={`account-order-${o.id}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div>
                    <div className="font-semibold text-ink">{o.order_number}</div>
                    <div className="text-xs text-ink-soft mt-0.5">{new Date(o.created_at).toLocaleString("en-PK")}</div>
                    <div className="text-sm text-ink-soft mt-1">{o.items.reduce((s, i) => s + i.quantity, 0)} items</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl text-ink">{formatPKR(o.total)}</div>
                    <span className={`mt-1 inline-block px-2 py-1 text-[10px] uppercase tracking-widest ${STATUS_STYLE[o.status] || ""}`}>{o.status}</span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
